// src/pages/auth/Login.jsx
import { useState, useContext, useEffect } from "react";
import { useSearchParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaGoogle, FaFacebookF, FaMicrosoft } from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import FacebookLogin from "react-facebook-login";
import axios from "axios";
import "./login.css";
import { loginRequest } from "../../config/msalConfig";
import LoadingOverlay from "../../components/LoadingOverlay"; // Ajusta la ruta según corresponda

const Login = () => {
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get("role") || "user";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { updateUser } = useContext(AuthContext);
  const { instance } = useMsal();

  const apiUrl = import.meta.env.VITE_STRAPI_URL || "http://localhost:1337";

  useEffect(() => {
    setError(null);
  }, [roleParam]);

  // Google Login
  const googleLogin = useGoogleLogin({
    onSuccess: async ({ code }) => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${apiUrl}/api/google-custom?role=${roleParam}`,
          { code }
        );
        localStorage.setItem("jwt", response.data.jwt);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        updateUser(response.data.user);
        navigate(`/mi-cuenta?role=${roleParam}`);
      } catch (error) {
        console.error("Error en autenticación con Google:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message
        ) {
          setError(error.response.data.error.message);
        } else {
          setError("Error al autenticar con Google");
        }
      } finally {
        setLoading(false);
      }
    },
    flow: "auth-code",
  });

  // Microsoft Login
  const handleMicrosoftMsalLogin = async () => {
    setLoading(true);
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      if (loginResponse?.idToken) {
        const response = await axios.post(
          `${apiUrl}/api/microsoft-custom?role=${roleParam}`,
          { token: loginResponse.idToken },
          { headers: { "Content-Type": "application/json" } }
        );
        localStorage.setItem("jwt", response.data.jwt);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        updateUser(response.data.user);
        navigate(`/mi-cuenta?role=${roleParam}`);
      } else {
        console.error("No se encontró idToken en la respuesta de MSAL");
        setError("No se encontró idToken en la respuesta de MSAL");
      }
    } catch (error) {
      console.error("Error en login con Microsoft (MSAL):", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.message
      ) {
        setError(error.response.data.error.message);
      } else {
        setError("Error al autenticar con Microsoft");
      }
    } finally {
      setLoading(false);
    }
  };

  // Login local
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/auth/local`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: email, password }),
      });
      const data = await res.json();
      if (data.jwt) {
        localStorage.setItem("jwt", data.jwt);
        localStorage.setItem("user", JSON.stringify(data.user));
        updateUser(data.user);
        const userType = data.user.userType ? data.user.userType.toLowerCase() : "user";
        if (userType !== roleParam.toLowerCase()) {
          localStorage.removeItem("jwt");
          localStorage.removeItem("user");
          setError(
            `La cuenta que usaste es de ${
              userType === "employer" ? "Empleador" : "Usuario"
            }. Por favor, inicia sesión como ${userType}.`
          );
          return;
        }
        navigate(`/mi-cuenta?role=${roleParam}`);
      } else {
        const errorMsg = data.error?.message || "Error en el login";
        if (errorMsg.toLowerCase().includes("ya creaste una cuenta")) {
          setError(errorMsg);
        } else {
          setError(
            <>
              Correo electrónico y/o contraseña incorrecta. ¿Olvidaste tu contraseña?{" "}
              <RouterLink to="/forgot-password">Ingresa aquí</RouterLink>
            </>
          );
        }
      }
    } catch (err) {
      console.error("Error en el login local:", err);
      setError("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Facebook Login
  const facebookResponse = async (response) => {
    setLoading(true);
    try {
      console.log("Respuesta de Facebook:", response);
      const { accessToken } = response;
      if (!accessToken) {
        setError("No se obtuvo el accessToken de Facebook");
        return;
      }
      const { data } = await axios.post(
        `${apiUrl}/api/facebook-custom?role=${roleParam}`,
        { accessToken }
      );
      localStorage.setItem("jwt", data.jwt);
      localStorage.setItem("user", JSON.stringify(data.user));
      updateUser(data.user);
      navigate(`/mi-cuenta?role=${roleParam}`);
    } catch (error) {
      console.error("Error al autenticar con Facebook:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.error &&
        error.response.data.error.message
      ) {
        setError(error.response.data.error.message);
      } else {
        setError("Error al autenticar con Facebook");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-5">
      {loading && <LoadingOverlay message="Cargando..." color="#f5a623" />}
      <div className="card p-4 mx-auto" style={{ maxWidth: "600px" }}>
        <h2 className="text-center mb-4">
          Iniciar Sesión {roleParam === "employer" ? "(Empleador)" : "(Usuario)"}
        </h2>
        <form onSubmit={handleLogin}>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              Correo Electrónico
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="form-control"
              placeholder="Introduce tu Correo Electrónico"
            />
          </div>
          <div className="mb-3 position-relative">
            <label htmlFor="password" className="form-label">
              Contraseña
            </label>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-control"
              placeholder="Introduce tu contraseña"
            />
            <span
              style={{
                position: "absolute",
                right: "1rem",
                top: "70%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#555",
              }}
              onClick={toggleShowPassword}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          <div className="mb-3 text-end">
            <RouterLink to="/forgot-password" className="text-decoration-none">
              ¿Olvidaste la contraseña?
            </RouterLink>
          </div>
          {error && (
            <div className="alert alert-danger text-center">{error}</div>
          )}
          <div className="d-grid gap-2">
            <button type="submit" className="btn btn-primary">
              Iniciar Sesión
            </button>
          </div>
        </form>
        <hr className="my-4" />
        <div className="d-grid gap-2">
          <button type="button" className="btn btn-google" onClick={() => googleLogin()}>
            <FaGoogle className="me-2" /> Inicia sesión con Google
          </button>
          <FacebookLogin
            appId="1155198232519715"
            autoLoad={false}
            fields="name,email,picture"
            callback={facebookResponse}
            disableMobileRedirect={true}
            redirectUri="https://api.workingunidos.com/api/facebook-custom"
            textButton="Inicia sesión con Facebook"
            cssClass="btn btn-facebook"
            icon={<FaFacebookF className="me-2" />}
          />
          <button
            type="button"
            className="btn btn-info text-white btn-microsoft"
            onClick={() => handleMicrosoftMsalLogin()}
          >
            <FaMicrosoft className="me-2" /> Inicia sesión con Microsoft
          </button>
        </div>
        <div className="mt-4 text-center">
          {roleParam === "employer" ? (
            <p>
              ¿No eres empleador?{" "}
              <RouterLink to={`/login?role=user`}>Inicia sesión como Usuario</RouterLink>
            </p>
          ) : (
            <p>
              ¿No eres usuario?{" "}
              <RouterLink to={`/login?role=employer`}>
                Inicia sesión como Empleador
              </RouterLink>
            </p>
          )}
          <p>
            ¿No tienes una cuenta?{" "}
            <RouterLink to={`/register?role=${roleParam}`}>Regístrate</RouterLink>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;